import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@euroasiann/api-client';
import { TicketStatus, TicketEscalationLevel } from '@euroasiann/shared';
import { PageSkeleton } from '@euroasiann/shared/ui';

const TicketStatusBadge = ({ status }: { status: string }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case TicketStatus.OPEN: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100';
            case TicketStatus.IN_PROGRESS: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-100';
            case TicketStatus.RESOLVED: return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100';
            case TicketStatus.CLOSED: return 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18/40 dark:text-[hsl(var(--foreground))]';
            case TicketStatus.ESCALATED: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-100';
            default: return 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18/40 dark:text-[hsl(var(--foreground))]';
        }
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(status)}`}>
            {status?.replace?.('_', ' ') || status}
        </span>
    );
};

export const TechTicketDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [note, setNote] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchTicket = async () => {
        try {
            const response = await apiClient.getSupportTicketById('tech', id!);
            
            // Robust multi-layered mapping as requested by user
            const ticketData = 
                response?.data?.data?.ticket ?? 
                response?.data?.data ?? 
                response?.data ?? 
                {};
                
            setTicket(ticketData);
        } catch (error) {
            navigate('/support/tickets');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchTicket();
    }, [id]);

    const handleResolve = async () => {
        if (!note.trim()) {
            alert('Please provide a resolution note');
            return;
        }
        setIsProcessing(true);
        try {
            await apiClient.resolveSupportTicket('tech', id!, note);
            fetchTicket();
            setNote('');
        } catch (error) {
            console.error('Resolve failed', error);
            alert('Failed to resolve ticket');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEscalateToAdmin = async () => {
        if (!note.trim()) {
            alert('Please provide a note explaining the work done and why admin review is needed');
            return;
        }
        setIsProcessing(true);
        try {
            await apiClient.escalateSupportTicket('tech', id!, note);
            fetchTicket();
            setNote('');
        } catch (error) {
            console.error('Escalate failed', error);
            alert('Failed to escalate ticket to admin');
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading || !ticket) {
        return (
            <div className="p-8">
                <PageSkeleton variant="page" />
            </div>
        );
    }

    const canAct = ticket.status !== TicketStatus.RESOLVED &&
        ticket.status !== TicketStatus.CLOSED &&
        ticket.escalationLevel === TicketEscalationLevel.TECH_TEAM;

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <button
                onClick={() => navigate('/support/tickets')}
                className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
                &larr; Back to Dashboard
            </button>

            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row justify-between items-start border-b border-[hsl(var(--border))] pb-6 mb-6 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-2">{ticket.title}</h1>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                            Filed by <span className="font-medium text-[hsl(var(--foreground))]">{ticket.createdBy?.firstName} {ticket.createdBy?.lastName}</span> ({ticket.organizationId?.name})
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <TicketStatusBadge status={ticket.status} />
                        <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] px-2 py-1 bg-[hsl(var(--secondary))] rounded">
                            #{ticket?._id?.slice?.(-6).toUpperCase() || 'N/A'}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider block mb-1">Priority</label>
                        <p className="capitalize font-semibold text-[hsl(var(--foreground))]">{ticket.priority}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                        <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider block mb-1">Escalation Level</label>
                        <p className="capitalize font-semibold text-[hsl(var(--foreground))]">{ticket?.escalationLevel?.replace?.('_', ' ') || ticket?.escalationLevel || 'N/A'}</p>
                    </div>
                </div>

                <div className="rounded-xl bg-[hsl(var(--secondary))] p-6 border border-[hsl(var(--border))]">
                    <h3 className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase mb-3">Issue Description</h3>
                    <div className="text-[hsl(var(--foreground))] whitespace-pre-wrap leading-relaxed">
                        {ticket.description}
                    </div>
                </div>
            </div>

            {canAct && (
                <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6">
                    <h3 className="text-lg font-bold text-[hsl(var(--foreground))] mb-2">Resolution / Escalation</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                        Mark as resolved when the technical work is complete, or escalate to Admin with notes for final review.
                    </p>
                    <textarea
                        className="w-full border border-[hsl(var(--border))] rounded-xl p-4 bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all"
                        rows={4}
                        placeholder="Enter technical resolution steps, or notes for admin review..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="mt-4 flex flex-wrap justify-end gap-3">
                        <button
                            onClick={handleEscalateToAdmin}
                            disabled={isProcessing}
                            className="bg-orange-600 text-white px-6 py-2.5 rounded-xl font-semibold shadow-sm hover:bg-orange-700 disabled:opacity-50 transition-all"
                        >
                            {isProcessing ? 'Processing...' : 'Escalate to Admin'}
                        </button>
                        <button
                            onClick={handleResolve}
                            disabled={isProcessing}
                            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition-all"
                        >
                            {isProcessing ? 'Processing...' : 'Mark as Resolved'}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm p-6">
                <h3 className="text-sm font-bold text-[hsl(var(--muted-foreground))] uppercase mb-6">Ticket History</h3>
                <div className="space-y-6">
                    {(ticket?.history || []).slice().reverse().map((item: any, i: number) => (
                        <div key={i} className="flex gap-4 border-l-2 border-[hsl(var(--border))] pl-4 relative">
                            {/* Dot */}
                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[hsl(var(--border))] border-4 border-[hsl(var(--card))]" />

                            <div className="text-xs text-[hsl(var(--muted-foreground))] min-w-[100px] pt-0.5">
                                {new Date(item.timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm text-[hsl(var(--foreground))]">{item.performedBy?.firstName}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))]">
                                        {item.action}
                                    </span>
                                </div>
                                <p className="text-[hsl(var(--muted-foreground))] text-sm bg-[hsl(var(--secondary))]/50 p-3 rounded-lg border border-[hsl(var(--border))]/50">
                                    {item.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
